import { useState, useEffect, useCallback, useRef } from "react";
import { Phone, PhoneCall, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 5000; // every 5 seconds — cheap query, essential signal
const URGENT_SEC = 60;
const ALERT_SEC = 45; // chime + desktop notification fire at this wait threshold
const ALERT_ROLES = new Set(["super_admin", "admin", "operator", "user", "receptionist", "care_coordinator"]);

function fmtWait(sec) {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

function fmtPhone(n) {
  if (!n) return "Unknown";
  const digits = String(n).replace(/\D/g, "").slice(-10);
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return n;
}

/** Play a short, non-intrusive 2-note chime via Web Audio API.
 * Reuses the same pattern as ActiveCallOverlay so we don't ship a new mp3. */
function playHoldingChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    // Two soft sine beeps: 660Hz then 880Hz — pleasant, distinct from ringtone.
    [{ f: 660, t: 0 }, { f: 880, t: 0.18 }].forEach(({ f, t }) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, now + t);
      g.gain.exponentialRampToValueAtTime(0.35, now + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.16);
      o.connect(g); g.connect(ctx.destination);
      o.start(now + t); o.stop(now + t + 0.17);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 500);
  } catch { /* audio disabled — silent fallback */ }
}

/**
 * HoldingCallersBadge — ambient real-time signal that lives in the sidebar next
 * to NotificationBell. Polls /api/twilio/queue/holding every 5s and turns red
 * when any caller has been on hold for ≥ URGENT_SEC. Clicking it opens a
 * dropdown listing each holding caller with a Grab button.
 *
 * Design intent (per Dawn, 2026-07-21): "so a coordinator can proactively grab
 * a call before it drops to voicemail." Every second matters — this must be
 * visible everywhere the app is, not just on the Dashboard.
 */
export default function HoldingCallersBadge({ collapsed = false }) {
  const [data, setData] = useState({ count: 0, max_wait_seconds: 0, callers: [], critical: false });
  const [open, setOpen] = useState(false);
  const [busySid, setBusySid] = useState("");
  const [error, setError] = useState("");
  const menuRef = useRef(null);
  const alertedRef = useRef(new Set()); // call_sids we've already chimed for
  const [userRole, setUserRole] = useState("");

  // Snapshot current user role once so we can gate the active nudge.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("user");
      if (raw) setUserRole(JSON.parse(raw).role || "");
    } catch { /* ignore malformed storage */ }
  }, []);

  // Request desktop-notification permission once on first mount (only for
  // coordinator-ish roles who will actually work these alerts).
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (!ALERT_ROLES.has(userRole)) return;
    if (Notification.permission === "default") {
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }
  }, [userRole]);

  const load = useCallback(async () => {
    if (document.hidden) return; // pause polling when the tab is backgrounded
    try {
      const res = await fetch(`${API}/api/twilio/queue/holding`, {
        credentials: "include",
      });
      if (!res.ok) return;
      const next = await res.json();
      setData(next);
      setError("");

      // Active nudge: any caller who has crossed the ALERT_SEC threshold and
      // hasn't chimed yet fires a chime + desktop notification. We stamp the
      // call_sid so a lingering caller doesn't re-chime every 5 seconds.
      if (ALERT_ROLES.has(userRole)) {
        const seen = alertedRef.current;
        const freshlyUrgent = (next.callers || []).filter(
          (c) => c.wait_seconds >= ALERT_SEC && !seen.has(c.call_sid),
        );
        if (freshlyUrgent.length > 0) {
          playHoldingChime();
          freshlyUrgent.forEach((c) => {
            seen.add(c.call_sid);
            try {
              if (typeof window !== "undefined" && "Notification" in window &&
                  Notification.permission === "granted") {
                const n = new Notification("Caller still holding", {
                  body: `${fmtPhone(c.from_number)} · ${fmtWait(c.wait_seconds)} on hold`,
                  tag: `holding-${c.call_sid}`,
                  icon: "/favicon.ico",
                  requireInteraction: false,
                });
                setTimeout(() => { try { n.close(); } catch { /* ignore */ } }, 8000);
              }
            } catch { /* ignore notification errors */ }
          });
        }
        // Garbage-collect: drop call_sids that are no longer holding so they'll
        // chime again on a future call (e.g. same phone calls back tomorrow).
        const activeSids = new Set((next.callers || []).map((c) => c.call_sid));
        for (const sid of Array.from(seen)) {
          if (!activeSids.has(sid)) seen.delete(sid);
        }
      }
    } catch { /* ignore polling errors */ }
  }, [userRole]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const grab = async (call_sid) => {
    setBusySid(call_sid); setError("");
    try {
      const res = await fetch(`${API}/api/twilio/queue/grab`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ call_sid }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "Grab failed");
      toast.success(j.bridged ? "Connecting you now…" : "Caller claimed. Answer via the phone panel.");
      load();
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    }
    setBusySid("");
  };

  const count = data.count || 0;
  const hasHolding = count > 0;
  const urgent = data.critical || data.max_wait_seconds >= URGENT_SEC;
  const dotColor = !hasHolding ? "" : urgent ? "bg-red-500" : "bg-yellow-400";

  // If nothing holding, render a compact idle chip (helps confirm the signal
  // is alive) — but only in expanded mode. In collapsed mode, render nothing
  // when idle so the sidebar isn't cluttered.
  if (!hasHolding && collapsed) return null;

  return (
    <div ref={menuRef} className="relative" data-testid="holding-callers-badge">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="holding-callers-toggle"
        title={hasHolding ? `${count} caller${count === 1 ? "" : "s"} holding` : "No callers holding"}
        className={
          collapsed
            ? "relative flex justify-center items-center p-2 rounded-lg w-full hover:bg-yellow-500/20 transition-colors"
            : "relative flex items-center gap-3 w-full p-2 rounded-lg hover:bg-yellow-500/20 transition-colors"
        }
        style={{ color: "white" }}
      >
        <div className="relative">
          <PhoneCall className={`w-5 h-5 ${urgent && hasHolding ? "animate-pulse" : ""}`} />
          {hasHolding && (
            <span
              className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-black rounded-full text-white ${dotColor}`}
              data-testid="holding-count"
            >
              {count}
            </span>
          )}
        </div>
        {!collapsed && (
          <div className="flex-1 text-left">
            <div className="text-[11px] font-bold uppercase tracking-wider opacity-70">
              {hasHolding ? "Holding now" : "Queue clear"}
            </div>
            <div className="text-sm font-semibold" data-testid="holding-summary">
              {hasHolding
                ? `${count} caller${count === 1 ? "" : "s"} · longest ${fmtWait(data.max_wait_seconds || 0)}`
                : "No one on hold"}
            </div>
          </div>
        )}
      </button>

      {open && (
        <div
          className="absolute left-full ml-2 top-0 bg-white rounded-xl shadow-2xl border border-slate-200 w-[340px] z-50 overflow-hidden"
          data-testid="holding-callers-menu"
        >
          <div className="p-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
            <PhoneCall className="w-4 h-4 text-slate-500" />
            <div className="text-sm font-bold text-slate-800">
              {hasHolding ? `${count} caller${count === 1 ? "" : "s"} holding` : "Queue clear"}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="ml-auto text-slate-400 hover:text-slate-700"
              data-testid="holding-callers-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && (
            <div className="p-2 text-xs text-red-700 bg-red-50 border-b border-red-100">{error}</div>
          )}

          <div className="max-h-[380px] overflow-y-auto">
            {!hasHolding && (
              <div className="p-6 text-center text-xs text-slate-400" data-testid="holding-empty">
                No callers are currently on hold.
              </div>
            )}
            {(data.callers || []).map((c) => {
              const isUrgent = c.wait_seconds >= URGENT_SEC;
              return (
                <div
                  key={c.call_sid}
                  className="p-3 border-b border-slate-100 last:border-0 flex items-center gap-3"
                  data-testid={`holding-caller-${c.call_sid}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-800 truncate">{fmtPhone(c.from_number)}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <span className={isUrgent ? "text-red-600 font-bold" : ""}>{fmtWait(c.wait_seconds)}</span>
                      {c.company_name && (<><span className="text-slate-300">·</span><span className="truncate">{c.company_name}</span></>)}
                    </div>
                  </div>
                  <button
                    onClick={() => grab(c.call_sid)}
                    disabled={busySid === c.call_sid}
                    data-testid={`holding-grab-${c.call_sid}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1 disabled:opacity-60 ${isUrgent ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                  >
                    {busySid === c.call_sid ? <Loader2 className="w-3 h-3 animate-spin" /> : <Phone className="w-3 h-3" />}
                    Grab
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
